import React, { useState, useEffect, useRef } from "react";
import { DatePicker, Select, Table, Card, Spin, Input, Tag, Button, Tooltip, Collapse, Pagination, Modal } from "antd";
import { SearchOutlined, FilterOutlined, ReloadOutlined, DownOutlined, UpOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { ThumbsUp, ThumbsDown, HelpCircle } from "lucide-react"; // Using Lucide icons for better emoji representation
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Databases, Query } from "appwrite";
import appwriteClient from '../appwrite.config';
import Navbar from "../components/Navbar";
import { MapPin } from "lucide-react";
import "./ActivityWall.css"; // You'll need to create this CSS file
import FleetProfileCreation from "./FleetProfileCreation";
import { Star,Ban,CircleHelp } from 'lucide-react';
import VisitingNotePreview from "./VisitingNotepreview";


const { RangePicker } = DatePicker;
const { Option } = Select;
const { Panel } = Collapse;

const ActivityWall = () => {
    const databases = new Databases(appwriteClient);
    
    // State variables
    const [activityData, setActivityData] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fleets, setFleets] = useState([]);
    const [users, setUsers] = useState([]);
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50, // Changed to 50 as requested
    });
    
    // Fleet Profile modal state
    const [isFleetProfileModalVisible, setIsFleetProfileModalVisible] = useState(false);
    const [updationId, setUpdationId] = useState(null);
    const [visitingNoteId,setVsitingNoteId] = useState(null);
    const [visitingNotePreviewVisible,setVsitingNotePreviewVisible] = useState(false);

    
    // Animation state for new entries
    const [newEntryId, setNewEntryId] = useState(null);
    
    // Filter states
    const [dateRange, setDateRange] = useState([
      dayjs().subtract(7, 'day'),
      dayjs()
    ]);
    const [selectedFleet, setSelectedFleet] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedActivityType, setSelectedActivityType] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    
    // Activity types derived from your schema
    const activityTypes = [
      "Tyre Fitment", 
      "Tyre Removal", 
      "Tyre Tracking", 
      "Fleet Profile Creation", 
      "Workorder Creation Started",
      "Workorder Creation Ended",
      "User Login",
      "User Logout",
      "Break Start",
      "Break End",
      "Tyre Report"
    ];
  


    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
const [locationPopupVisible, setLocationPopupVisible] = useState(false);
const [currentLocation, setCurrentLocation] = useState({ latitude: null, longitude: null });


const handleTyreReportClick = (record) => {
  if (record.activityType === 'Tyre Report' && record.tyreRemovalReport) {
    setCurrentPdfUrl(record.tyreRemovalReport.replace('http://', 'https://'));
    setPdfViewerVisible(true);
  }
};


const handleLocationClick = (record, e) => {
  e.stopPropagation(); // Prevent row click
  if (record.geoLatitude && record.geoLongitude) {
    setCurrentLocation({
      latitude: record.geoLatitude,
      longitude: record.geoLongitude
    });
    setLocationPopupVisible(true);
  }
};

// Close PDF viewer
const closePdfViewer = () => {
  setPdfViewerVisible(false);
  setCurrentPdfUrl(null);
};

const closeLocationPopup = () => {
  setLocationPopupVisible(false);
  setCurrentLocation({ latitude: null, longitude: null });
};

    // Tag options with new emojis
    const tagOptions = [
      { value: "ok", label: <Star color="green"/>, color: "green" },   // Thumbs up
      { value: "down", label: <Ban color="red"/>, color: "red" },   // Thumbs down
      { value: "question", label: <CircleHelp color="orange"/>, color: "orange" }, // Question mark
    ];
  
    // Fetch initial data
    useEffect(() => {
      fetchFleets();
      fetchUsers();
      fetchActivityData(); // Fetch activities on component mount
    }, []);
    
    // Re-fetch when pagination changes
    useEffect(() => {
      fetchActivityData();
    }, [pagination.current]);

    // Group activity data by date whenever raw data changes
    useEffect(() => {
      if (activityData.length > 0) {
        groupActivityDataByDate();
      }
    }, [activityData]);
  
    // Set up realtime subscription
    useEffect(() => {
      const unsubscribe = appwriteClient.subscribe([
        'databases.AppwriteCloudFEApp.collections.ActivityLogRegister.documents'
      ], (response) => {
        // Handle realtime updates
        if (response.events.includes('databases.*.collections.*.documents.*.create')) {
          // Add new activity to the list
          const newActivity = response.payload;
          // Only add if it matches the current filters
          if (matchesCurrentFilters(newActivity)) {
            setActivityData(prevData => [newActivity, ...prevData]);
            setTotalDocuments(prev => prev + 1);
            setNewEntryId(newActivity.$id); // Set ID for animation
            toast.info(`New activity: ${newActivity.activityType} for ${newActivity.fleetName}`);
            
            // Reset animation after 5 seconds
            setTimeout(() => {
              setNewEntryId(null);
            }, 5000);
          }
        }
        
        if (response.events.includes('databases.*.collections.*.documents.*.update')) {
          // Update existing activity
          const updatedActivity = response.payload;
          setActivityData(prevData => prevData.map(item => 
            item.$id === updatedActivity.$id ? updatedActivity : item
          ));
        }
        
        if (response.events.includes('databases.*.collections.*.documents.*.delete')) {
          // Remove deleted activity
          const deletedId = response.payload.$id;
          setActivityData(prevData => {
            const newData = prevData.filter(item => item.$id !== deletedId);
            // Adjust total count
            if (newData.length !== prevData.length) {
              setTotalDocuments(prev => Math.max(0, prev - 1));
            }
            return newData;
          });
        }
      });
      
      // Cleanup subscription on component unmount
      return () => {
        unsubscribe();
      };
    }, [selectedFleet, selectedUser, selectedActivityType, dateRange]);
  
    // Group activity data by date
    const groupActivityDataByDate = () => {
      // Create a map of dates to activities
      const dateGroups = {};
      
      activityData.forEach(activity => {
        const activityDate = activity.date ? activity.date.replace(/\//g, '-') : 'Unknown Date';
        if (!dateGroups[activityDate]) {
          dateGroups[activityDate] = [];
        }
        dateGroups[activityDate].push(activity);
      });
      
      // Sort dates in descending order (newest first)
      const sortedDates = Object.keys(dateGroups).sort((a, b) => {
        if (a === 'Unknown Date') return 1;
        if (b === 'Unknown Date') return -1;
        return b.localeCompare(a);
      });
      
      // Create array of objects for the table
      const grouped = [];
      sortedDates.forEach(date => {
        // Add date header row
        grouped.push({
          $id: `date-${date}`,
          isDateHeader: true,
          date,
          activities: dateGroups[date]
        });
        
        // Add activity rows for this date
        dateGroups[date].forEach(activity => {
          grouped.push({
            ...activity,
            parentDate: date
          });
        });
      });
      
      setGroupedData(grouped);
    };
  
    // Check if an activity matches current filter settings
    const matchesCurrentFilters = (activity) => {
      const activityDate = activity.date ? activity.date.replace(/\//g, '-') : '';
      
      // Date range check
      if (dateRange && dateRange.length === 2) {
        const startDate = dateRange[0].format("YYYY-MM-DD");
        const endDate = dateRange[1].format("YYYY-MM-DD");
        if (activityDate < startDate || activityDate > endDate) {
          return false;
        }
      }
      
      // Fleet check
      if (selectedFleet && activity.fleetName !== selectedFleet) {
        return false;
      }
      
      // User check
      if (selectedUser && activity.userId !== selectedUser) {
        return false;
      }
      
      // Activity type check
      if (selectedActivityType && activity.activityType !== selectedActivityType) {
        return false;
      }
      
      // Search value check (now only for user names as we removed the other columns)
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        const matchesUserName = getUserNameById(activity.userId).toLowerCase().includes(searchLower);
          
        if (!matchesUserName) {
          return false;
        }
      }
      
      return true;
    };
  
    // Fetch fleet data
    const fetchFleets = async () => {
      try {
        const response = await databases.listDocuments("AppwriteCloudFEApp", "65ea96670e2c9740a984",[Query.limit(1000)]);
        // Extract unique fleet names
        const uniqueFleets = Array.from(new Set(
          response.documents.map(doc => doc.fleetname).filter(Boolean)
        )).map(fleetname => ({ $id: fleetname, fleetname }));
        
        setFleets(uniqueFleets);
      } catch (error) {
        toast.error("Error fetching fleets");
        console.error("Error fetching fleets:", error);
      }
    };
  
    // Fetch user data
    const fetchUsers = async () => {
      try {
        const response = await databases.listDocuments("AppwriteCloudFEApp", "UserRegister",[Query.limit(1000)]);
        setUsers(response.documents);
      } catch (error) {
        toast.error("Error fetching users");
        console.error("Error fetching users:", error);
      }
    };
  
    // Fetch activity data based on filters
    const fetchActivityData = async () => {
      if (!dateRange || dateRange.length !== 2) {
        toast.warning("Please select a date range");
        return;
      }
      
      try {
        setLoading(true);
        
        // Build query array for Appwrite
        let queries = [
          Query.greaterThanEqual("date", dateRange[0].format("YYYY/MM/DD")),
          Query.lessThanEqual("date", dateRange[1].format("YYYY/MM/DD")),
          Query.orderDesc("$createdAt"),
          Query.limit(pagination.pageSize),
          Query.offset((pagination.current - 1) * pagination.pageSize)
        ];
        
        // Add optional filters
        if (selectedFleet) {
          queries.push(Query.equal("fleetName", selectedFleet));
        }
        
        if (selectedUser) {
          queries.push(Query.equal("userId", selectedUser));
        }
        
        if (selectedActivityType) {
          queries.push(Query.equal("activityType", selectedActivityType));
        }

        // Create search filter for userId using user names
        if (searchValue) {
          const searchLower = searchValue.toLowerCase();
          // Check if any user name matches the search
          const matchingUsers = users.filter(user => {
            const displayName = user.displayName || '';
            const name = user.name || '';
            const email = user.email || '';
            return displayName.toLowerCase().includes(searchLower) ||
                   name.toLowerCase().includes(searchLower) ||
                   email.toLowerCase().includes(searchLower);
          });
          
          // If we found matching users, add their IDs to the search criteria
          if (matchingUsers.length > 0) {
            const userIds = matchingUsers.map(user => user.email || user.$id);
            // Use OR for user IDs
            if (userIds.length === 1) {
              queries.push(Query.equal("userId", userIds[0]));
            } else if (userIds.length > 1) {
              // Appwrite doesn't support OR queries directly, so we'll filter manually later
              // But we can at least narrow the results with one user
              queries.push(Query.equal("userId", userIds[0]));
            }
          }
        }

        // First get total count
        const countQueries = [...queries];
        // Remove pagination for count query
        const limitIndex = countQueries.findIndex(q => q.includes('limit'));
        if (limitIndex !== -1) countQueries.splice(limitIndex, 1);
        const offsetIndex = countQueries.findIndex(q => q.includes('offset'));
        if (offsetIndex !== -1) countQueries.splice(offsetIndex, 1);

        // Get document count
        const countResponse = await databases.listDocuments(
          "AppwriteCloudFEApp", 
          "ActivityLogRegister", 
          countQueries
        );
        
        // Now get paginated data
        const response = await databases.listDocuments(
          "AppwriteCloudFEApp", 
          "ActivityLogRegister", 
          queries             
        );
        
        // Further filter as needed based on search value
        let filteredData = response.documents;
        if (searchValue) {
          const searchLower = searchValue.toLowerCase();
          filteredData = filteredData.filter(item => {
            // Search by user name (we already added userId filter in the query)
            const userName = getUserNameById(item.userId);
            const matchesUserName = userName.toLowerCase().includes(searchLower);
            
            return matchesUserName;
          });
        }
        
        // Set the total document count for pagination
        setTotalDocuments(countResponse.total);
        
        // Add index property for row striping and default status if missing
        setActivityData(filteredData.map((item, index) => ({
          ...item, 
          index,
          status: item.status || "pending" // Default to pending if no status
        })));
      } catch (error) {
        toast.error("Error fetching activity data");
        console.error("Error fetching activity data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Handle tag update
    const handleTagUpdate = async (record, tagValue) => {
      try {
        setLoading(true);
        
        await databases.updateDocument(
          "AppwriteCloudFEApp",
          "ActivityLogRegister",
          record.$id,
          { tag: tagValue }
        );
        
        // Update local state after successful DB update
        setActivityData(prevData => 
          prevData.map(item => 
            item.$id === record.$id ? { ...item, tag: tagValue } : item
          )
        );
        
        toast.success(`Tag updated to ${tagValue}`);
      } catch (error) {
        toast.error("Error updating tag");
        console.error("Error updating tag:", error);
      } finally {
        setLoading(false);
      }
    };

    // Handle row click to mark as seen and open fleet profile
  const handleRowClick = async (record) => {
  // Skip if it's a date header row
  if (record.isDateHeader) return;
  
  try {
    // Don't open profile for click on tag buttons (handled by event propagation)
    if (window.tagButtonClicked) {
      window.tagButtonClicked = false;
      return;
    }

    // Handle Tyre Report click first
    if (record.activityType === 'Tyre Report' && record.tyreRemovalReport) {
      handleTyreReportClick(record);
      return;
    }

    // Only update if status is pending
    if (record.status === "pending") {
      // Update status to seen
      await databases.updateDocument(
        "AppwriteCloudFEApp",
        "ActivityLogRegister",
        record.$id,
        { status: "seen" }
      );
      
      // Update local state
      setActivityData(prevData => 
        prevData.map(item => 
          item.$id === record.$id ? { ...item, status: "seen" } : item
        )
      );
    }

    // Open fleet profile modal
    if(record.activityType=='Visiting Note'){
      console.log(record)
      setVsitingNoteId(record.visitingNotes);
      setVsitingNotePreviewVisible(true);
    }
    if(record.activityType=='Fleet Profile Creation'){
      console.log(record)
      setUpdationId(record.fleetProfile.$id);
      setIsFleetProfileModalVisible(true);
    }

  } catch (error) {
    toast.error("Error updating record");
    console.error("Error updating record:", error);
  }
};
  
    // Get activity tag color based on type
    const getActivityTypeColor = (type) => {
      switch (type) {
        case 'Tyre Fitment': return 'green';
        case 'Tyre Removal': return 'red';
        case 'Tyre Tracking': return 'blue';
        case 'Fleet Profile Creation': return 'orange';
        case 'Workorder Creation Started': return 'purple';
        case 'Workorder Creation Ended': return 'pink';
        case 'Tyre Report' : return 'yellow';
        default: return 'default';
      }
    };
  
    // Reset filters
    const resetFilters = () => {
      setDateRange([
        dayjs().subtract(7, 'day'),
        dayjs()
      ]);
      setSelectedFleet(null);
      setSelectedUser(null);
      setSelectedActivityType(null);
      setSearchValue("");
      setPagination({ ...pagination, current: 1 });
      
      // Fetch with reset filters
      setTimeout(fetchActivityData, 0);
    };
  
    // Handle filter changes and fetch data
    const handleApplyFilters = () => {
      setPagination({ ...pagination, current: 1 }); // Reset to first page when filters are applied
      fetchActivityData();
    };
    
    // Handle pagination change
    const handleTableChange = (page) => {
      setPagination({ ...pagination, current: page });
    };
    
    // Get row class based on status, tag and animation
    const getRowClassName = (record) => {
      let classes = ['h-4'];
      
      // Skip date header rows
      if (record.isDateHeader) {
        return 'date-header bg-gray-100';
      }
      
      // Status-based font weight
      if (record.status === 'pending') {
        classes.push('font-bold');
      } else if (record.status === 'seen') {
        classes.push('font-normal');
      }
      
      // Tag-based styling
      if (record.tag === 'ok') {
        classes.push('bg-green-50');
      } else if (record.tag === 'down') {
        classes.push('bg-red-50');
      } else if (record.tag === 'question') {
        classes.push('bg-yellow-50');
      }
      
      // Animation for new entries
      if (record.$id === newEntryId) {
        classes.push('animate-highlight');
      }
      
      // Add alternating row color if no tag
      if (!record.tag && record.index % 2 === 0) {
        classes.push('row-highlight');
      }
      
      return classes.join(' ');
    };
    
    // Render tag cell with emoji buttons
    const renderTagCell = (_, record) => {
      // Don't render for date headers
      if (record.isDateHeader) return null;
      
      return (
        <div className="flex space-x-2">
          {tagOptions.map((tag) => (
            <Button
              key={tag.value}
              className={`p-1 ${record.tag === tag.value ? 'bg-gray-200' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                window.tagButtonClicked = true;
                handleTagUpdate(record, tag.value);
              }}
            >
              <span className="text-lg">{tag.label}</span>
            </Button>
          ))}
        </div>
      );
    };
    
    // Find user name from userId
    const getUserNameById = (userId) => {
      if (!userId) return "Unknown";
      const user = users.find(u => u.email === userId || u.$id === userId);
      return user ? (user.displayName || user.name || user.email || userId) : userId;
    };
    
    // Format date for date header
    const formatDateHeader = (dateStr) => {
      if (dateStr === 'Unknown Date') return 'Unknown Date';
      
      const date = dayjs(dateStr);
      const today = dayjs();
      const yesterday = dayjs().subtract(1, 'day');
      
      if (date.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')) {
        return `Today - ${date.format('DD MMM YYYY')}`;
      } else if (date.format('YYYY-MM-DD') === yesterday.format('YYYY-MM-DD')) {
        return `Yesterday - ${date.format('DD MMM YYYY')}`;
      } else {
        return date.format('dddd, DD MMM YYYY');
      }
    };
    
    // Table columns with conditional rendering for date headers
    const columns = [
  {
    title: 'Activity Type',
    dataIndex: 'activityType',
    key: 'activityType',
    render: (text, record) => {
      if (record.isDateHeader) {
        return (
          <div className="font-semibold text-lg">
            {formatDateHeader(record.date)}
            <span className="text-sm text-gray-500 ml-2">
              ({record.activities.length} activities)
            </span>
          </div>
        );
      }
      return <Tag color={getActivityTypeColor(text)}>{text}</Tag>;
    },
    onFilter: (value, record) => record.activityType === value,
    colSpan: (record) => record.isDateHeader ? 6 : 1, // Updated colSpan to 6
  },
  {
    title: 'Fleet',
    dataIndex: 'fleetName',
    key: 'fleetName',
    render: (text, record) => {
      if (record.isDateHeader) return null;
      else if(record.activityType=="Fleet Profile Creation"){
        return record.fleetProfile?.fleetname
      }
      return text;
    },
    colSpan: (record) => record.isDateHeader ? 0 : 1,
  },
  {
    title: 'Time',
    key: 'time',
    render: (_, record) => {
      if (record.isDateHeader) return null;
      return record.time?.slice(0,5)|| '-';
    },
    colSpan: (record) => record.isDateHeader ? 0 : 1,
  },
  {
    title: 'User',
    dataIndex: 'userId',
    key: 'userId',
    render: (userId, record) => {
      if (record.isDateHeader) return null;
      return getUserNameById(userId);
    },
    colSpan: (record) => record.isDateHeader ? 0 : 1,
  },
  // NEW LOCATION COLUMN
  {
    title: 'Location',
    key: 'location',
    render: (_, record) => {
      if (record.isDateHeader) return null;
      if (record.geoLatitude && record.geoLongitude) {
        return (
          <Tooltip title="View Location">
            <Button
              type="text"
              icon={<MapPin size={18} color="#e74c3c" />}
              onClick={(e) => handleLocationClick(record, e)}
              className="p-1 hover:bg-gray-100 rounded-full"
            />
          </Tooltip>
        );
      }
      return '-';
    },
    colSpan: (record) => record.isDateHeader ? 0 : 1,
  },
  {
    title: 'Tag',
    key: 'tag',
    render: renderTagCell,
    colSpan: (record) => record.isDateHeader ? 0 : 1,
  },
];
  
    return (
      <>
        <div className="p-0 overflow-scroll max-h-dvh">
          <div className='w-full'><Navbar page={"Home"}/></div>

          <Spin tip="Loading..." spinning={loading} size="large">
            <div className="bg-white p-2 px-4 rounded-lg mx-2">
              <div className="flex justify-between items-center ">
                <h2 className="text-xl font-semibold">Activity Wall</h2>
                <Button 
                  onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                  icon={filtersCollapsed ? <DownOutlined /> : <UpOutlined />}
                >
                  {filtersCollapsed ? "Show Filters" : "Hide Filters"}
                </Button>
              </div>
              
              {!filtersCollapsed && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  
                  <div className="flex items-end space-x-2">
                    <Button 
                      type="primary" 
                      icon={<FilterOutlined />} 
                      onClick={handleApplyFilters}
                      className="bg-blue-600 text-white px-4 py-2 h-auto flex items-center"
                    >
                      Apply Filters
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={resetFilters}
                      className="px-4 py-2 h-auto flex items-center"
                    >
                      Reset
                    </Button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                    <RangePicker
                      className="w-full"
                      value={dateRange}
                      format="YYYY-MM-DD"
                      onChange={(dates) => setDateRange(dates)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fleet</label>
                    <Select
                      className="w-full"
                      mode="multiple"
                      placeholder="Select Fleet"
                      allowClear
                      onChange={setSelectedFleet}
                      value={selectedFleet}
                    >
                      {fleets.map(fleet => (
                        <Option key={fleet.$id} value={fleet.fleetname}>
                          {fleet.fleetname}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <Select
                      className="w-full"
                      placeholder="Select User"
                      mode="multiple"
                      allowClear
                      onChange={setSelectedUser}
                      value={selectedUser}
                    >
                      {users.map(user => (
                        <Option key={user.$id} value={user.email}>
                          {user.displayName || user.email}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
                    <Select
                      className="w-full"
                      mode="multiple"
                      placeholder="Select Activity Type"
                      allowClear
                      onChange={setSelectedActivityType}
                      value={selectedActivityType}
                    >
                      {activityTypes.map(type => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search by User</label>
                    <Input
                      placeholder="Enter user name"
                      prefix={<SearchOutlined />}
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {activityData.length === 0 ? (
              <div className="text-center p-8 bg-white rounded-lg shadow-md mx-2">
                <p className="text-lg text-gray-500">No activity records found.</p>
                <p className="text-sm text-gray-400">Try adjusting your filters or date range.</p>
              </div>
            ) : (
              <div className="mx-2">
                <Card className="shadow-md">
                  
                  
                  <Table 
                    dataSource={groupedData} 
                    columns={columns}
                    rowKey="$id"
                    pagination={false}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    rowClassName={getRowClassName}
                    onRow={(record) => ({
                      onClick: () => handleRowClick(record),
                      style: record.isDateHeader ? {} : { cursor: 'pointer' }
                    })}
                  />
                  <div className="mt-4 flex justify-end">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={totalDocuments}
                      onChange={handleTableChange}
                      showSizeChanger={false}
                      showTotal={(total) => `Total ${total} items`}
                    />
                  </div>
                </Card>
              </div>
            )}
          </Spin>
          <Modal
  title="Tyre Report"
  open={pdfViewerVisible}
  onCancel={closePdfViewer}
  width="90%"
  style={{ top: 20 }}
  bodyStyle={{ height: '80vh', padding: 0 }}
  footer={null}
>
  {currentPdfUrl && (
    <iframe
      src={currentPdfUrl}
      style={{ width: '100%', height: '100%', border: 'none' }}
      title="Tyre Report PDF"
    />
  )}
</Modal>

{/* Location Popup Modal */}
<Modal 
  title="Location Details" 
  open={locationPopupVisible} 
  onCancel={closeLocationPopup} 
  width={600}
  footer={[
    <Button key="close" onClick={closeLocationPopup}>
      Close
    </Button>,
    <Button 
      key="external-maps" 
      type="primary" 
      onClick={() => {
        const { latitude, longitude } = currentLocation;
        window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
      }}
      disabled={!currentLocation.latitude || !currentLocation.longitude}
    >
      Open in Google Maps
    </Button>
  ]}
>
  {currentLocation.latitude && currentLocation.longitude && (
    <div>

      
      {/* Embedded Google Map */}
      <div className="mt-4">
        <iframe
          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBR4Z3kCrFWW6fnwScXcyqGlKAgUEh7AcU&q=${currentLocation.latitude},${currentLocation.longitude}&zoom=15`}
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  )}
</Modal>
          {/* Fleet Profile Modal */}
          <Modal
            title="Fleet Profile"
            open={isFleetProfileModalVisible}
            onCancel={() => {setIsFleetProfileModalVisible(false);setUpdationId(null)}}
            width="80%"
            footer={null}
          >
            <FleetProfileCreation isUpdation={true} id={updationId} onSuccess={()=>{setIsFleetProfileModalVisible(false);fetchActivityData();setUpdationId(null)}}/>
          </Modal>
          <Modal
            title="Visitng Note"
            open={visitingNotePreviewVisible}
            onCancel={() => {setVsitingNotePreviewVisible(false);setVsitingNoteId(null)}}
            width="80%"
            footer={null}
          >
            <VisitingNotePreview visitingRecord={visitingNoteId}/>
          </Modal>
        </div>
        
        {/* Add this CSS to your ActivityWall.css file */}
        <style jsx>{`
          .date-header {
            background-color: #f3f4f6;
            font-weight: 600;
          }
          
          .font-bold {
            font-weight: 700;
          }
          
          .font-light {
            font-weight: 300;
          }
          
          .animate-highlight {
            animation: highlight 5s;
          }
          
          @keyframes highlight {
            0% { background-color: rgba(59, 130, 246, 0.3); }
            100% { background-color: transparent; }
          }
        `}</style>
      </>
    );
};

export default ActivityWall;